import { apiService } from './apiService';

// ==========================================
// TYPES
// ==========================================

export type MessageType = 'text' | 'image' | 'gif' | 'system';

export interface MessageDto {
  id: string;
  matchId: string;
  senderProfileId: string;
  senderDisplayName: string;
  senderPhotoUrl?: string;
  type: MessageType;
  content: string;
  attachmentUrl?: string;
  sentAt: string;
  deliveredAt?: string;
  readAt?: string;
  replyToMessageId?: string;
  replyToMessage?: MessageDto;
  isOwnMessage: boolean;
}

export interface ConversationDto {
  matchId: string;
  otherProfileId: string;
  otherDisplayName: string;
  otherPhotoUrl?: string;
  otherIsVerified: boolean;
  lastMessage?: MessageDto;
  unreadCount: number;
  matchedAt: string;
  lastMessageAt?: string;
  isSuperLikeMatch: boolean;
}

export interface SendMessageRequest {
  matchId: string;
  content: string;
  type?: MessageType;
  attachmentUrl?: string;
  attachmentStorageKey?: string;
  replyToMessageId?: string;
}

export interface MessageResult {
  success: boolean;
  error?: string;
  message?: MessageDto;
  recipientProfileId?: string;
}

export interface MessagesResponse {
  messages: MessageDto[];
  hasMore: boolean;
}

export interface UnreadCountResponse {
  count: number;
}

// ==========================================
// API FUNCTIONS
// ==========================================

/**
 * Get all conversations for the current user.
 */
export async function getConversations(): Promise<ConversationDto[]> {
  const response = await apiService.get<ConversationDto[]>('/api/messages/conversations');
  return response.data ?? [];
}

/**
 * Get messages for a specific conversation.
 */
export async function getMessages(
  matchId: string,
  limit: number = 50,
  before?: string
): Promise<MessagesResponse> {
  let endpoint = `/api/messages/conversations/${matchId}?limit=${limit}`;
  if (before) {
    endpoint += `&before=${encodeURIComponent(before)}`;
  }
  const response = await apiService.get<MessagesResponse>(endpoint);
  return response.data ?? { messages: [], hasMore: false };
}

/**
 * Send a message in a conversation.
 */
export async function sendMessage(request: SendMessageRequest): Promise<MessageResult> {
  const response = await apiService.post<MessageResult>('/api/messages/send', {
    matchId: request.matchId,
    content: request.content,
    type: request.type ?? 'text',
    attachmentUrl: request.attachmentUrl,
    attachmentStorageKey: request.attachmentStorageKey,
    replyToMessageId: request.replyToMessageId,
  });
  return response.data ?? { success: false, error: response.error };
}

/**
 * Mark all messages in a conversation as read.
 */
export async function markAsRead(matchId: string): Promise<boolean> {
  const response = await apiService.post<{ success: boolean }>(`/api/messages/conversations/${matchId}/read`);
  return response.data?.success ?? false;
}

/**
 * Delete a message.
 */
export async function deleteMessage(messageId: string): Promise<boolean> {
  const response = await apiService.delete<{ success: boolean }>(`/api/messages/${messageId}`);
  return response.data?.success ?? false;
}

/**
 * Get total unread message count.
 */
export async function getUnreadCount(): Promise<number> {
  const response = await apiService.get<UnreadCountResponse>('/api/messages/unread-count');
  return response.data?.count ?? 0;
}

// ==========================================
// MESSAGE SERVICE OBJECT
// ==========================================

export const messageService = {
  getConversations,
  getMessages,
  sendMessage,
  markAsRead,
  deleteMessage,
  getUnreadCount,
};
